import React from 'react';
import {
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesIconButton,
  AgnesPageHeader,
} from '@agnes/ui';
import { Eye, SlidersHorizontal } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useParametrosStore } from '@/stores/useParametrosStore';
import { authStorage } from '@/lib/authStorage';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';

type Parametro = Record<string, any>;

// Cada categoria de parâmetro de análise abre a rotina de configuração correspondente.
// A chave é o `config` (slug estável do BFF), não o nome exibido.
const DESTINO_POR_CONFIG: Record<string, string> = {
  cbo_risk_classification: '/esocial/cbo/classificacao-de-analise',
  work_schedules: '/esocial/horarios/classificacao-de-analise',
  rubric_analysis_classification: '/esocial/rubricas/classificacao-de-analise',
  rubric_incidence_by_nature: '/esocial/rubricas/incidencias-por-natureza',
};

// TODO: precisa de API — fallback mock removido; dados reais vêm do BFF (useParametrosStore).
const parametrosMock: Parametro[] = [];

export default function PageParametrosAnalise() {
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(50);
  const lista = useParametrosStore((s) => s.lista);
  const carregando = useParametrosStore((s) => s.carregando);
  const navigate = useNavigate();

  React.useEffect(() => {
    // só busca no BFF se autenticado; sem token (dev) usa fallback mock e evita redirect ao login
    if (authStorage.getItem('access_Token')) useParametrosStore.getState().buscar();
  }, []);

  const data: Parametro[] = lista.length ? lista : parametrosMock;

  const columns: AgnesDataTableColumn<Parametro>[] = [
    { key: 'nome', header: 'Nome', width: 500, render: (r) => r.name ?? r.label ?? r.nome ?? '-' },
    { key: 'alteradoPor', header: 'Alterado por', width: 220, render: (r) => r.updatedBy ?? '-' },
    { key: 'atualizacao', header: 'Atualização', width: 220, render: (r) => r.updatedAt ?? '-' },
    {
      key: 'acoes',
      header: 'Ações',
      width: 90,
      align: 'center',
      render: (r) => {
        const destino = DESTINO_POR_CONFIG[r.config];
        return (
          <AgnesIconButton
            aria-label="Visualizar"
            data-qa={`parametros-acao-visualizar-${r.config}`}
            size="sm"
            disabled={!destino}
            onClick={() => destino && navigate(destino)}
          >
            <Eye className="h-4 w-4" />
          </AgnesIconButton>
        );
      },
    },
  ];

  return (
    <>
      <AgnesPageHeader
        icon={<SlidersHorizontal />}
        title="Parâmetros de análise"
        subtitle="Cadastro e gestão de recomendações de análise"
        breadcrumbs={[
          { label: 'Home', href: '/home' },
          { label: 'Configurações' },
          { label: 'eSocial' },
          { label: 'Parâmetros de análise' },
        ]}
      />

      <AgnesCard>
        <AgnesCardContent className="flex items-stretch">
          <TableWrapper>
            <AgnesDataTable<Parametro>
              columns={columns}
              data={data}
              rowKey={(r) => String(r.id ?? r.name)}
              loading={carregando}
              pagination={{
                page,
                pageSize,
                total: data.length,
                onPageChange: setPage,
                onPageSizeChange: (s) => {
                  setPageSize(s);
                  setPage(0);
                },
              }}
            />
          </TableWrapper>
        </AgnesCardContent>
      </AgnesCard>
    </>
  );
}
